import {
  getRedirectTransaction,
  hasRedirectParameters,
  isRegistrationFlowType,
} from './redirectTransaction.utils';
import {
  clearRegistrationRedirect,
  getRegistrationRedirect,
  isRegistrationRedirectActive,
  isRegistrationRedirectInFlight,
  markRegistrationRedirectMsalReady,
  setRegistrationRedirectMsalReadyHandoff,
  type RegistrationRedirectReadyState,
  type RegistrationRedirectSessionState,
} from './registrationRedirectState.utils';

/**
 * Entra account exists after sign-up, but UserService profile is created later on account-setup.
 * Skip MSAL restore → completeLogin during that handoff to avoid expected 404 noise and cache clears.
 */
export function shouldDeferMsalBackendValidation(): boolean {
  if (isRegistrationRedirectActive()) {
    return true;
  }

  const txn = getRedirectTransaction();
  if (txn && isRegistrationFlowType(txn.flowType)) {
    return true;
  }

  if (hasRedirectParameters() && window.location.pathname.startsWith('/registration')) {
    return true;
  }

  return false;
}

/**
 * Marks registration MSAL redirect complete for AccountSetup completion.
 * Called from MsalRedirectHandler after token acquisition succeeds.
 */
export function finalizeRegistrationMsalRedirectHandoff(
  accessToken: string,
  registrationRedirect: RegistrationRedirectSessionState | null = getRegistrationRedirect(),
): RegistrationRedirectReadyState {
  const markedReady = markRegistrationRedirectMsalReady(accessToken);
  if (markedReady) {
    return markedReady;
  }

  return setRegistrationRedirectMsalReadyHandoff({
    accessToken,
    flow: registrationRedirect?.flow ?? 'existing',
    contactEmail: registrationRedirect?.contactEmail,
  });
}

export interface RegistrationEntraErrorRedirectResult {
  isRegistrationFlow: boolean;
}

/**
 * Clears registration redirect state when Entra returns ?error= during registration.
 */
export function handleRegistrationEntraErrorRedirect(): RegistrationEntraErrorRedirectResult {
  const isRegistrationFlow = isRegistrationRedirectInFlight();
  if (isRegistrationFlow) {
    clearRegistrationRedirect();
  }

  return { isRegistrationFlow };
}
